import { Flex, Skeleton, SkeletonCircle, SkeletonText, VStack } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { type ReactElement, useState } from 'react'

import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import { HasuraContextWrapper } from '@/client/context/hasura'
import { useUser } from '@/client/context/user'
import useInterval from '@/client/hooks/useInterval'
import { type Query, SQL } from '@/client/query'

import Card from '@/components/Worlds/UI/Card'

import BreakoutSessionJoinBanner from './internal/BreakoutSessionJoinBanner'
import BreakoutSessionManageBanner from './internal/BreakoutSessionManageBanner'

const TUTOR_EARLY_ACCESS_MINUTES = 15
const LEARNER_EARLY_ACCESS_MINUTES = 0
const REFRESH_INTERVAL_SECONDS = 1

function BreakoutSessionBannerSkeleton({ isUserTutor }: { isUserTutor: boolean }): ReactElement {
  return isUserTutor ? (
    <Card width="100%" mb={4}>
      <VStack align="stretch" spacing={6}>
        <Skeleton height="32px" width="180px" />
        <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
          <SkeletonText mt="2" noOfLines={3} spacing="4" skeletonHeight="20px" width="100%" />
          <SkeletonText mt="2" noOfLines={3} spacing="4" skeletonHeight="20px" width="100%" />
        </Flex>
      </VStack>
    </Card>
  ) : (
    <Card width="100%" mb={4}>
      <VStack spacing={6} align="center" py={4}>
        <SkeletonCircle size="16" />
        <SkeletonText mt="2" noOfLines={2} spacing="4" skeletonHeight="20px" width="100%" />
        <Skeleton height="40px" width="120px" borderRadius="md" />
      </VStack>
    </Card>
  )
}

export default function BreakoutSessionBanner({ series }: { series: Query }): ReactElement | null {
  const user = useUser()
  const [showManageBanner, setShowManageBanner] = useState(false)
  const [showJoinBanner, setShowJoinBanner] = useState(false)

  const isUserTutor = series(SeriesQ.isHost({ userId: user.id })) ?? false
  const isUserRegistered = series(SeriesQ.isUserRegistered(user.id)) ?? false
  const isParticipant = isUserRegistered || isUserTutor

  const nextSession = series.one(
    SQL`${SeriesQ.getSessions({}, SQL`${SessionQ.end_date} > CURRENT_TIMESTAMP`)} LIMIT 1`
  )

  const sessionId = nextSession(SessionQ.id) ?? 0
  const sessionStartDate = nextSession(SessionQ.start_date)

  const isBreakoutEnabledSession = nextSession(SessionQ.is_breakout_enabled) ?? false
  useInterval(
    () => {
      if (sessionStartDate == null) {
        return
      }

      const currentTime = DateTime.now()
      const startTime = DateTime.fromISO(sessionStartDate)

      setShowManageBanner(
        isUserTutor && startTime.minus({ minutes: TUTOR_EARLY_ACCESS_MINUTES }) < currentTime
      )

      setShowJoinBanner(startTime.minus({ minutes: LEARNER_EARLY_ACCESS_MINUTES }) < currentTime)
    },
    REFRESH_INTERVAL_SECONDS * 1000,
    [showManageBanner, showJoinBanner, isUserTutor, sessionStartDate]
  )

  if (!isParticipant || sessionStartDate == null || !isBreakoutEnabledSession) {
    return null
  }

  return (
    <HasuraContextWrapper
      LoadingComponent={() => <BreakoutSessionBannerSkeleton isUserTutor={isUserTutor} />}
    >
      {showManageBanner ? <BreakoutSessionManageBanner sessionId={sessionId} /> : null}
      {showJoinBanner ? <BreakoutSessionJoinBanner sessionId={sessionId} /> : null}
    </HasuraContextWrapper>
  )
}
